import { zonedTimeToUtc, utcToZonedTime, getTimezoneOffset } from 'date-fns-tz'
import { format, parse, parseISO } from 'date-fns'

export const formInputTimeFormat = 'HH:mm'
export const formInputDateFormat = 'ISO'

export const zodInputTimeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/

/**
 * @desc ISO 8601 string format
 */
export const zodInputDateRegex =
  /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))/

export const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

export const getCurrentDateInZone = (timezone: string, date?: Date) => {
  return utcToZonedTime(date ?? new Date(), timezone)
}

export const isISOString = (val: string) => {
  const d = new Date(val)
  return !Number.isNaN(d.valueOf()) && d.toISOString() === val
}

export const formatUTCDateInZone = ({
  date,
  timezone,
  formatStr,
}: {
  date: Date | string
  timezone: string
  formatStr?: string | 'ISO'
}) => {
  formatStr = formatStr ?? 'h:mma'

  const zoneDate = utcDateToDate(date, timezone)

  if (formatStr === 'ISO') return zoneDate.toISOString()

  return format(zoneDate, formatStr)
}

export const formatUTCDate = ({
  date,
  formatStr,
}: {
  date: Date | string
  formatStr?: string | 'ISO'
}) => {
  formatStr = formatStr ?? 'h:mma'

  //creating a new Date() will shift the date to the current timezone,
  //so subtract the current offset to create a new Date object in the current zone,
  //with the same UTC date
  date = subtractZoneOffset(new Date(date), currentTimezone)

  if (formatStr === 'ISO') return date.toISOString()

  return format(date, formatStr)
}

export const addZoneOffset = (date: Date, timezone: string) => {
  date = new Date(date)
  date.setMilliseconds(getTimezoneOffset(timezone))

  return date
}

export const subtractZoneOffset = (date: Date, timezone: string) => {
  date = new Date(date)
  date.setMilliseconds(getTimezoneOffset(timezone) * -1)

  return date
}

export const dateToUTCDate = (date: Date | string, timezone: string) => {
  return zonedTimeToUtc(date, timezone)
}

export const utcDateToDate = (date: Date | string, timezone: string) => {
  return utcToZonedTime(date, timezone)
}

/**
 * @param {string} time - must be in the format HH:MM - ie 22:30 === 10:30pm
 */
export const parseTimeString = (time: string, date?: Date) => {
  return parse(time, 'H:m', date ?? new Date())
}

/**
 * @param {string} date - must be an ISO 8601 date string
 */
export const parseISOString = (date: string) => {
  return parseISO(date)
}

/**
 * @desc parse the ISO string, but in the current zone (ex 9:00 -> 9:00 GMT-0400 (Eastern Daylight Time))
 */
export const parseISOStringWithoutTimezone = (date: string) => {
  return parse(date, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'", new Date())
}

export const getNowISOString = () => {
  //toISOString() will shift the current date by the current timezone offset,
  //so we need to add the offset back to get the current date in ISO string format
  return addZoneOffset(new Date(), currentTimezone).toISOString()
}

/* ----------------- FORM UTILS ----------------- */

export const serverDateStringToFormInputTimeString = (
  date: string,
  timezone: string
) => {
  return formatUTCDateInZone({
    date: date,
    timezone: timezone,
    formatStr: formInputTimeFormat,
  })
}

/**
 * @param {string} date - must be an ISO 8601 date string
 * @desc our date picker (Flatpickr) takes an ISO string and creates a date object, which will
 * shift the date to the current zone. So to offset this shift, we add the zone offset to our UTC date first
 * super weird, but seems to work
 */
export const serverDateStringToFormInputDateString = (
  date: string,
  timezone: string
) => {
  return formatUTCDateInZone({
    date: subtractZoneOffset(utcToZonedTime(date, timezone), timezone),
    timezone: timezone,
    formatStr: formInputDateFormat,
  })
}

/* ----------------- /FORM UTILS ----------------- */

/**
 * @param {string} time - must be in the format HH:MM - ie 22:30 === 10:30pm
 */
export const timeStringToUTCDate = (
  time: string,
  timezone: string,
  date?: Date
) => {
  return dateToUTCDate(parseTimeString(time, date), timezone)
}

export const resetTime = (date: Date) => {
  date.setHours(0)
  date.setMinutes(0)
  date.setSeconds(0)
  date.setMilliseconds(0)

  return date
}

export const dayNumberToName = (day: number) => {
  switch (day) {
    case 0:
      return 'Sun.'
    case 1:
      return 'Mon.'
    case 2:
      return 'Tues.'
    case 3:
      return 'Wed.'
    case 4:
      return 'Thurs.'
    case 5:
      return 'Fri.'
    case 6:
      return 'Sat.'
    default:
      return ''
  }
}

export const combineDate = (date1: Date, date2: Date) => {
  date1 = new Date(date1.getTime())
  date1.setHours(date2.getHours())
  date1.setMinutes(date2.getMinutes())
  date1.setSeconds(date2.getSeconds())
  date1.setMilliseconds(date2.getMilliseconds())

  return date1
}

export const diaryDate = (date: Date) => {
  date.setHours(6)
  date.setMinutes(0)
  date.setSeconds(0)
  date.setMilliseconds(0)

  return date
}

export const diaryDateISOString = (date: Date) => {
  date.setHours(6)
  date = new Date(date.getTime() - 60000 * date.getTimezoneOffset())

  date.setMinutes(0)
  date.setSeconds(0)
  date.setMilliseconds(0)

  return date.toISOString()
}

export const getStartOfDay = (date: Date) => {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

export const getEndOfDay = (date: Date) => {
  const d = new Date(date)
  d.setHours(23, 59, 59, 999)
  return d
}

export const getStartOfMonth = (date: Date) => {
  const d = new Date(date)
  d.setDate(1)
  d.setHours(0, 0, 0, 0)
  return d
}

export const getEndOfMonth = (date: Date) => {
  const d = new Date(date)
  // Move to the first day of the next month, then subtract one millisecond
  d.setMonth(d.getMonth() + 1, 1)
  d.setHours(0, 0, 0, 0)
  d.setMilliseconds(-1)
  return d
}

export const getStartOfYear = (date: Date) => {
  const d = new Date(date)
  d.setMonth(0, 1) // January 1st
  d.setHours(0, 0, 0, 0)
  return d
}

export const getEndOfYear = (date: Date) => {
  const d = new Date(date)
  d.setMonth(0, 1) // January 1st of the next year
  d.setFullYear(d.getFullYear() + 1)
  d.setHours(0, 0, 0, 0)
  d.setMilliseconds(-1) // Go back 1ms to get the end of the current year
  return d
}
